import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { propsToDom } from 'core/helpers';
import { Label, smallCSS } from 'styles/typography';
import { ContainerGrid } from 'components/Container';
import { rem } from 'styles/utils';
import { Panel } from 'components/Panel';
import media from 'styles/media';
import { RichText } from 'components/RichText';
import { Button } from 'components/Button';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { Icon } from 'components/Icon';

const ListComponent = ({ id, primary, items, slice_type, next_slice_type, ...others }) => {
  const { background, label } = primary;
  const hasLabel = !!label?.text;

  return (
    <Panel {...propsToDom(others)} id={slice_type} nextId={next_slice_type} background={background}>
      <ContainerGrid>
        <Left>{hasLabel && <Label as="h2">{label.text}</Label>}</Left>
        <Right>
          <Accordion allowMultipleExpanded allowZeroExpanded preExpanded={[0]}>
            {items.map(
              ({ item_title, secondary_title, text, button, button_text, button_color }, index) => {
                const hasButton = !!button?.url && button_text;
                const hasText = !!text?.richText?.length > 0;
                return (
                  <Item key={index} uuid={index}>
                    <ItemHeading>
                      <ItemButton>
                        <span>{item_title?.text}</span>
                        <span>{secondary_title}</span>
                        <Icon id="arrow-down-big" />
                      </ItemButton>
                    </ItemHeading>
                    <ItemPanel>
                      {hasText && <RichText render={text.richText} />}
                      {hasButton && (
                        <Button icon="arrow-right" size="small" color={button_color} {...button}>
                          {button_text}
                        </Button>
                      )}
                    </ItemPanel>
                  </Item>
                );
              }
            )}
          </Accordion>
        </Right>
      </ContainerGrid>
    </Panel>
  );
};

const Item = styled(AccordionItem)`
  --border-color: ${({ theme }) => theme.colors.black};
  border-bottom: 1px solid var(--border-color);

  &:first-child {
    border-top: 1px solid var(--border-color);
  }

  [data-background='dark'] & {
    --border-color: ${({ theme }) => theme.colors.white};
  }
`;

const ItemHeading = styled(AccordionItemHeading)``;

const ItemButton = styled(AccordionItemButton)`
  display: flex;
  align-items: center;
  padding: ${rem(24)} 0;
  cursor: pointer;

  span:nth-child(1) {
    margin-right: auto;
  }

  span:nth-child(2) {
    margin-left: ${rem(24)};
    opacity: 0.5;
  }

  ${Icon} {
    font-size: ${rem(16)};
    margin-left: ${rem(24)};
  }

  &[aria-expanded='true'] ${Icon} {
    transform: scaleY(-1);
  }

  ${media.mobile`
    padding: ${rem(16)} 0;

    ${Icon} {
      font-size: ${rem(12)};
    }
  `}
`;

const ItemPanel = styled(AccordionItemPanel)`
  padding-top: ${rem(24)};
  padding-bottom: ${rem(64)};

  ${RichText} {
    ${smallCSS};
  }

  ${Button} {
    margin-top: ${rem(24)};
  }

  ${media.mobile`
    padding-top: ${rem(16)};
    padding-bottom: ${rem(48)};
  `}
`;

const Left = styled.div`
  grid-column: 1 / span 2;

  ${media.mobile`
    grid-column: 1 / -1;
  `}
`;

const Right = styled.div`
  grid-column: 3 / span 5;

  ${media.mobile`
    grid-column: 1 / -1;
  `}
`;

export const List = styled(ListComponent)`
  ${media.mobile`
    ${ContainerGrid} {
      grid-row-gap: ${rem(32)};
    }
  `}
`;

export const query = graphql`
  fragment SliceListHome on PrismicHomeDataBodyList {
    id
    slice_type
    primary {
      background
      label {
        text
      }
    }
    items {
      item_title {
        text
      }
      secondary_title
      text {
        richText
      }
      button {
        url
        target
      }
      button_text
      button_color
    }
  }

  fragment SliceListPage on PrismicPageDataBodyList {
    id
    slice_type
    primary {
      background
      label {
        text
      }
    }
    items {
      item_title {
        text
      }
      secondary_title
      text {
        richText
      }
      button {
        url
        target
      }
      button_text
      button_color
    }
  }
`;
